import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import { TimesIcon } from '@c2fo/liquidity/icons';
import { OPERATIONAL_ALERT_PLACEMENT, type AlertArtifactContent, type Artifact } from '@/enterprise/data/useArtifacts';
import { useReporting } from '@/reporting';
import { renderInlineMarkdownBody } from './inlineMarkdownBody';

export { OPERATIONAL_ALERT_PLACEMENT };

export interface OperationalAlertProps {
  body: string;
  /** Authored in CMT. Anything other than `MARKDOWN` renders the body exactly as typed. */
  bodyFormat: AlertArtifactContent['bodyFormat'];
  dismissible: boolean;
  tracking: Artifact['tracking'];
  onDismiss: () => void;
}

/**
 * AppLayout sticky operational alert driven by SEA `ALERT` artifacts.
 * Chrome matches `HolidayDelayBanner` (and CMT `OperationalAlertPreviewTemplate`).
 */
const OperationalAlert = ({ body, bodyFormat, dismissible, tracking, onDismiss }: OperationalAlertProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();

  useEffectOnce(() => {
    trackEnterpriseEvent('operational-alert::shown', {
      location: 'app-layout',
      source: 'artifact',
      artifactUuid: tracking.artifactUuid,
      assetUuid: tracking.assetUuid,
      campaignUuid: tracking.campaignUuid,
      campaignRunUuid: tracking.campaignRunUuid,
    });
  });

  return (
    <div className="h-10 w-full bg-yellow-200 px-4 py-2 sm:py-0">
      <div className="flex h-full flex-row items-center justify-between md:justify-center">
        <p className="flex-1 text-center text-xs sm:text-left sm:text-sm md:text-center md:text-base">
          {renderInlineMarkdownBody(body, bodyFormat)}
        </p>
        {dismissible && (
          <button
            aria-label={t('core.close')}
            className="z-20 ml-4 p-1 text-xl transition-all duration-200 hover:bg-black/10"
            type="button"
            onClick={onDismiss}
          >
            <TimesIcon className="h-7 w-7 fill-text-primary" />
          </button>
        )}
      </div>
    </div>
  );
};

export default OperationalAlert;
